/**
 * I10 — per-character consumable slot bindings for the PartyBar
 * consumable-mode toggle.
 *
 * Each character gets **6 slots**. A slot either holds an `itemId` (the
 * character has pre-assigned this consumable from their inventory) or
 * is empty. Slots have their **own cooldown**, independent of the
 * attack-cooldown ATB, so a potion can be drunk while the main hand is
 * still rolling.
 *
 * Consumable slots are a *view* into the character's inventory — they
 * do not own items. The inventory panel is the source of truth for
 * quantities; the slot just remembers which item is mapped to slot N.
 */
export const CONSUMABLE_SLOTS_PER_CHARACTER = 6;

/** Default cooldown per consumable use in seconds (tune later). */
export const DEFAULT_CONSUMABLE_COOLDOWN_SEC = 4;

interface CharacterSlots {
  /** Length === CONSUMABLE_SLOTS_PER_CHARACTER. `null` = empty slot. */
  readonly bindings: (string | null)[];
  /** Remaining cooldown seconds per slot, parallel array. */
  readonly cooldowns: number[];
}

const byCharacter = new Map<string, CharacterSlots>();

function ensure(characterId: string): CharacterSlots {
  let slots = byCharacter.get(characterId);
  if (!slots) {
    slots = {
      bindings: new Array<string | null>(CONSUMABLE_SLOTS_PER_CHARACTER).fill(null),
      cooldowns: new Array<number>(CONSUMABLE_SLOTS_PER_CHARACTER).fill(0),
    };
    byCharacter.set(characterId, slots);
  }
  return slots;
}

export const ConsumableSlotsStore = {
  getBindings(characterId: string): readonly (string | null)[] {
    return ensure(characterId).bindings;
  },

  getCooldowns(characterId: string): readonly number[] {
    return ensure(characterId).cooldowns;
  },

  /**
   * Bind a consumable to slot `index` for this character. Pass `null`
   * to clear. Out-of-range indices are silently ignored so the UI can
   * always call through safely.
   */
  bind(characterId: string, index: number, itemId: string | null): void {
    if (index < 0 || index >= CONSUMABLE_SLOTS_PER_CHARACTER) return;
    const slots = ensure(characterId);
    slots.bindings[index] = itemId;
  },

  /**
   * True when the slot is holding an item AND its cooldown has
   * elapsed. Empty / cooling-down slots return false.
   */
  isReady(characterId: string, index: number): boolean {
    if (index < 0 || index >= CONSUMABLE_SLOTS_PER_CHARACTER) return false;
    const slots = ensure(characterId);
    return slots.bindings[index] !== null && (slots.cooldowns[index] ?? 0) <= 0;
  },

  /** Start the cooldown for a slot (called by the future use-action). */
  triggerCooldown(characterId: string, index: number, seconds = DEFAULT_CONSUMABLE_COOLDOWN_SEC): void {
    if (index < 0 || index >= CONSUMABLE_SLOTS_PER_CHARACTER) return;
    const slots = ensure(characterId);
    slots.cooldowns[index] = seconds;
  },

  /**
   * Advance every cooldown by `dtSeconds`. Hook this into the same RAF
   * loop that drives the ATB bar; respects world-freeze as long as the
   * caller does.
   */
  tick(dtSeconds: number): void {
    if (dtSeconds <= 0) return;
    for (const slots of byCharacter.values()) {
      for (let i = 0; i < slots.cooldowns.length; i++) {
        if ((slots.cooldowns[i] ?? 0) > 0) {
          slots.cooldowns[i] = Math.max(0, (slots.cooldowns[i] ?? 0) - dtSeconds);
        }
      }
    }
  },

  clear(): void {
    byCharacter.clear();
  },
};
